/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useMemo} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {getVinData} from '@ion/core/src/services/Connect';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseVehicleProfileStatusResult = UseDefaultQueryResult & {
  isTestVin?: boolean;
  refresh: () => void;
};

/**
 * Retrieves the vehicle-profile environment for a VIN.
 */
export const useVehicleProfileStatus = (
  vin: string,
  staleTime = getStaleTime(5, false, false)
): UseVehicleProfileStatusResult => {
  const queryClient = useQueryClient();
  const key         = useMemo(() => (
    createKey(QueryKey.VEHICLE_PROFILE_STATUS, vin)
  ), [vin]);
  const query       = useQuery(key, async() => {
    const response = await getVinData(`vin=${encodeURIComponent(vin)}`);
    return response?.data?.response?.results?.find(({vin: resultVin}) => resultVin === vin)?.isTestVin;
  }, {
    staleTime : staleTime as number,
    enabled   : !!vin
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries(key);
  }, [key]);

  return createQueryResult(query, {
    isTestVin : query.data,
    refresh
  }) as UseVehicleProfileStatusResult;
};

export default useVehicleProfileStatus;
